import styled, { css, keyframes } from 'styled-components'
import { BadgeInternalProps, BadgePosition } from './types'
import { darken } from 'polished'

const getTransitionFor = (properties: string, duration: number = 1) =>
  css`
    transition-duration: ${duration}s;
    transition-property: ${properties};
    transition-timing-function: cubic-bezier(
      ${0.4 * duration},
      ${0.5 * duration},
      0,
      ${duration}
    );
  `

const widthProportion = 2.875
const titleAnimation = keyframes`
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  75% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`

export const RoundBadge = styled.div.attrs((props: BadgeInternalProps) => {
  props.loading = props.loading ? 1 : 0
  return props
})`
  ${getTransitionFor(
    'height, width, font-size, bottom, left, right, top, transform, border-radius'
  )};

  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  background-color: ${({ bottomBarColor }) => bottomBarColor};
  box-shadow: 0 8px 10px 0 rgba(0, 0, 0, 0.22);
  cursor: pointer;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  font-size: ${({ height }) => (height as number) / 2}px;
  height: ${({ height }) => height}px;
  overflow: hidden;
  position: fixed;
  text-overflow: ellipsis;
  width: ${({ height, loading, menuWidth, openCTA }) =>
    openCTA
      ? menuWidth
      : (height as number) * (loading ? 1 : widthProportion)}px;

  /* Setting defaults for transition animation */
  left: ${({ margin }) => margin}px;
  top: ${({ margin }) => margin}px;

  ${({ position, margin, height, openCTA }) => {
    switch (position) {
      case BadgePosition.TopLeft:
        return css`
          left: ${margin}px;
          top: ${margin}px;
          transform: none;
          border-radius: ${openCTA
            ? '10px 10px 0 0'
            : `${(height as number) + 2}px`};
        `
      case BadgePosition.TopRight:
        return css`
          left: 100%;
          right: ${margin}px;
          top: ${margin}px;
          transform: translateX(calc(-100% - ${margin}px));
          border-radius: ${openCTA
            ? '10px 10px 0 0'
            : `${(height as number) + 2}px`};
        `
      case BadgePosition.BottomLeft:
        return css`
          left: ${margin}px;
          top: 100%;
          bottom: ${margin}px;
          transform: translateY(calc(-100% - ${margin}px));
          border-radius: ${openCTA
            ? '0 0 10px 10px'
            : `${(height as number) + 2}px`};
        `
      case BadgePosition.BottomRight:
      default:
        return css`
          left: 100%;
          right: ${margin}px;
          top: 100%;
          bottom: ${margin}px;
          transform: translateX(calc(-100% - ${margin}px))
            translateY(calc(-100% - ${margin}px));
          border-radius: ${openCTA
            ? '0 0 10px 10px'
            : `${(height as number) + 2}px`};
        `
    }
  }};

  & > div:first-child {
    align-items: center;
    display: flex;
    text-overflow: ellipsis;
    height: ${({ height }) => height}px;
    width: ${({ height, loading }) =>
      (height as number) * (loading ? 1 : widthProportion)}px;

    > div {
      position: relative;
      z-index: 100;

      &:nth-child(1) {
        ${({ height, loading }) =>
          loading
            ? css`
                width: ${height}px;
              `
            : css`
                margin-left: ${(height as number) / 3}px;
                margin-right: ${(height as number) / 6}px;
              `}
        text-align: center;
      }
      &:nth-child(2) {
        ${getTransitionFor('color')};
        animation: ${titleAnimation} 1s ease-out;
        color: ${({ fontColor }) => fontColor};
        display: ${({ loading }) => (loading ? 'none' : 'block')};
        font-size: ${({ height }) => (height as number) / 3.4}px;
        line-height: ${({ height }) => (height as number) / 3.4}px;
        em {
          display: block;
          font-size: ${({ height }) => (height as number) / 2.7}px;
          font-style: normal;
          font-weight: bold;
          line-height: ${({ height }) => (height as number) / 2.7}px;
          margin: 0;
        }
      }
    }
  }

  > div:last-child {
    ${getTransitionFor('height, width', 0.5)};

    z-index: 0;
    position: relative;
    ${({ openCTA, showProgress, progress }) => {
      if (openCTA && showProgress) {
        return progress && progress >= 0 && progress <= 1.0
          ? css`
              width: ${(progress as number) * 100}%;
              transition-delay: 1s;
            `
          : css`
              width: ${progress}%;
              transition-delay: 1s;
            `
      }
      return css`
        width: 100%;
      `
    }}
    height: 100%;
    top: ${({ height }) => -(height as number)}px;

    > div {
      transition: width 0.6s ease-out;
      background: ${({ bgColor, hovering }) =>
        hovering ? darken(0.145, bgColor as string) : bgColor};
      width: 100%;
      height: 100%;
    }
  }
`
